import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { paths } from 'shared/routing/paths';
import { LogOut } from 'lucide-react';
import { Logo } from 'shared/components/Logo';
import { useAuth } from 'shared/contexts/AuthContext';
import { SupportBotButton } from 'shared/components/AppShell/SupportBotButton';
import { ONBOARDING_HELP } from './onboardingHelp';
import { OnboardingHelpPanel } from './OnboardingHelpPanel';

const STEP_LABELS: Array<{ path: string; label: string }> = [
  { path: paths.onboarding.root, label: 'Account' },
  { path: paths.onboarding.expertise, label: 'Expertise' },
  { path: paths.onboarding.hourlyRate, label: 'Hourly Rate' },
  { path: paths.onboarding.idVerification, label: 'Identity Verification' },
];

/**
 * Onboarding wizard shell: a minimal header (logo + step label + support +
 * sign out), NOT the full AppShell/EXPERT_NAV sidebar — mirrors aligned's
 * dedicated `expert_onboarding_layout` (a thin wrapper, not the expert home
 * shell).
 *
 * The support button is deliberately part of this header rather than of any one
 * step. Onboarding is where an expert is least able to help themselves: they
 * have no access to the rest of the app, so every other route's help control is
 * out of reach, and the one place they most need it is also the one place they
 * can get permanently stuck — a declined identity check offers no retry, because
 * no retry can change the answer.
 *
 * The current step is derived directly from the route path. Upstream tracks
 * a `_selectedExpertOnboardingPage` index that's set inconsistently across
 * steps (0/1/1/2/3/3) — deliberately not ported.
 */
export function OnboardingLayout() {
  const { userAccount, logout } = useAuth();
  const location = useLocation();

  // Upstream re-checks this at both the startup gate and expert-home-layout
  // mount so a completed expert can't land back on the wizard.
  if (userAccount?.raterOnboardingComplete) {
    return <Navigate to={paths.expert.root} replace />;
  }

  const currentStep = STEP_LABELS.find((s) => s.path === location.pathname);
  const helpItems = ONBOARDING_HELP[location.pathname];

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="sticky top-0 z-30 h-16 bg-primary text-white">
        <div className="mx-auto flex h-full max-w-6xl items-center justify-between px-4 sm:px-6">
          <Logo className="text-xl" />
          <div className="flex items-center gap-4 text-sm">
            {currentStep ? <span className="hidden text-white/80 sm:inline">{currentStep.label}</span> : null}
            <SupportBotButton />
            <button
              type="button"
              onClick={() => void logout()}
              className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-1.5 font-medium hover:bg-white/20"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div
        className={`mx-auto w-full flex-1 px-4 py-8 sm:px-6 ${
          helpItems ? 'max-w-6xl lg:grid lg:grid-cols-[minmax(0,1fr)_18rem] lg:gap-12' : 'max-w-3xl'
        }`}
      >
        <div className="min-w-0 max-w-3xl">
          <Outlet />
        </div>
        {helpItems ? (
          <div className="mt-12 border-t border-slate-200 pt-8 lg:mt-0 lg:border-t-0 lg:pt-0">
            <OnboardingHelpPanel items={helpItems} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
